import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { beep } from '../../lib/realtime';
import { staffPath } from '../../lib/paths';

type Issue = { stationId: string; stationName: string; problem: 'no_printer' | 'offline' | 'failures' | 'stuck'; unprinted: number; failed: number };
export type ProductionHealth = { screens: number; issues: Issue[]; alarm: boolean };

/** Saúde da produção (setores só com impressora). Atualiza por evento (impressão/comanda) e, por garantia, a cada 20 s. */
export function useProductionHealth() {
  return useQuery({ queryKey: ['production-health'], queryFn: () => api<ProductionHealth>('/api/staff/production/health'), refetchInterval: 20_000 });
}

const REPEAT_MS = 2 * 60_000;

function message(i: Issue) {
  const what =
    i.problem === 'no_printer' ? `${i.stationName}: setor só com impressora, mas sem impressora configurada`
    : i.problem === 'offline' ? `Impressora do setor ${i.stationName} offline`
    : i.problem === 'failures' ? `Impressora do setor ${i.stationName} falhando (${i.failed} comanda(s) não impressa(s))`
    : `${i.stationName}: comanda(s) aguardando impressão`;
  return i.unprinted > 0 && i.problem !== 'failures' ? `${what} — ${i.unprinted} comanda(s) não impressa(s)` : what;
}

/**
 * Alerta fixo no topo de todas as telas da equipe. Num setor só com impressora, impressora parada = cozinha/bar sem
 * saber do pedido. Com pedido parado ele fica vermelho e apita (ao aparecer e a cada 2 min enquanto durar).
 */
export function ProductionAlert({ health, canManagePrinting, canOperate }: { health: ProductionHealth | undefined; canManagePrinting: boolean; canOperate: boolean }) {
  const lastBeep = useRef(0);
  const alarm = !!health?.alarm;
  useEffect(() => {
    if (!alarm) {
      lastBeep.current = 0;
      return;
    }
    const ring = () => {
      if (Date.now() - lastBeep.current < REPEAT_MS - 1000) return;
      lastBeep.current = Date.now();
      beep();
      setTimeout(beep, 700);
    };
    ring();
    const t = setInterval(ring, REPEAT_MS);
    return () => clearInterval(t);
  }, [alarm]);

  if (!health?.issues.length) return null;
  return (
    <div role="alert" className={alarm ? 'border-b border-danger-600 bg-danger-600 text-white' : 'border-b border-warn-600/30 bg-warn-50 text-warn-600'}>
      <div className="mx-auto flex max-w-5xl flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3 sm:px-8">
        <AlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 text-sm">
          {health.issues.map((i) => <p key={i.stationId} className="font-semibold">{message(i)}</p>)}
          <p className={alarm ? 'text-white/90' : 'text-ink-2'}>
            {alarm ? 'A cozinha/bar pode não estar recebendo os pedidos. Avise a equipe e confira a impressora.' : 'Sem pedidos parados agora, mas os próximos não serão impressos até resolver.'}
          </p>
        </div>
        <div className="flex gap-2 text-sm font-semibold">
          {canOperate && <Link to={staffPath('/producao')} className={alarm ? 'rounded-lg bg-white/15 px-3 py-1.5 hover:bg-white/25' : 'rounded-lg bg-white px-3 py-1.5 hover:bg-paper-2'}>Ver pedidos na tela</Link>}
          {canManagePrinting && <Link to={staffPath('/impressao')} className={alarm ? 'rounded-lg bg-white/15 px-3 py-1.5 hover:bg-white/25' : 'rounded-lg bg-white px-3 py-1.5 hover:bg-paper-2'}>Impressão</Link>}
        </div>
      </div>
    </div>
  );
}
